import React, { useContext, useEffect, useState } from 'react';

import { UserDataContext } from 'lane-shared/contexts';

import Property from 'components/builder/properties/input/Property';
import { H4, H5 } from 'components/typography';

import { ChannelIntegrationEditorProps } from './ChannelIntegrationEditorProps';

import styles from './ChannelIntegrationEditorAutoRenderer.scss';
import { IntegrationProviderEnum } from 'constants-integrations';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { LoadSettingsButton } from 'components/integrations/BuildingEnginesPrism/components/LoadSettingsButton';
import * as BuildingEnginesPrism from 'lane-shared/helpers/integrations/BuildingEnginesPrism';
import { useTranslation } from 'react-i18next';
import { PackagedTypeEnum } from 'lane-shared/types/properties/PackagedTypeEnum';
import { PropertyUIType } from 'lane-shared/types/properties/PropertyUI';
import {
  PropertyOptionType,
  PropertiesInterface,
} from 'lane-shared/types/properties/Property';
import { Checkbox } from 'design-system-web';

enum IntegrationInputs {
  LoadSettingsButton = 'loadSettingsButton',
  SubHeading = 'subHeading',
}

interface DropdownField {
  _id: string;
  _order: number;
  name: string;
  value: string;
}

interface MultiSelectDropdownField {
  label: string;
  value: string;
}

export default function ChannelIntegrationEditorAutoRenderer({
  definition,
  channel,
  channelIntegration,
  onUpdateChannelIntegration,
  integration,
  forCreate,
}: ChannelIntegrationEditorProps) {
  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);

  const [metaDataLoading, setMetaDataLoading] = useState(false);
  const [metaDataError, setMetaDataError] = useState('');
  const [metadataPopulated, setMetadataPopulated] = useState(false);

  const isBEFFEnabled = useFlag(
    FeatureFlag.EnableSSOAndMultiBuildingForBE,
    false
  );

  const isBEWebhookEnabled = useFlag(
    FeatureFlag.WebHookNotificationForBE,
    false
  );

  const providerValue = integration?.name;

  // Function to validate multi-select dropdown fields by checking if the selected values exist in the dropdown options.
  const isSelectedFieldsExistsInDropdown = (
    dropdownFields: DropdownField[],
    selectedFields: MultiSelectDropdownField[]
  ): boolean => {
    // Check that each selected field exists in the dropdown fields
    // Note: For now, we are converting types just to be safe side
    return selectedFields.every(selectedField => {
      const exists = dropdownFields.some(
        dropdownField =>
          String(dropdownField.value) === String(selectedField.value) &&
          String(dropdownField.name) === String(selectedField.label)
      );

      return exists;
    });
  };

  const setupMultiSelectValidators = (
    property: PropertyUIType<PropertyOptionType>,
    dataToSet: any,
    key: string,
    settings: ChannelIntegrationEditorProps['channelIntegration']['settings']
  ) => {
    if (!property || !settings) return;

    const selectedValue = settings[key];

    property.validators = property.validators || [];

    // Ensure only one 'In' validator exists, and update it
    const validatorMap = new Map(property.validators.map(v => [v.name, v]));

    validatorMap.set('In', { name: 'In', value: dataToSet });
    property.validators = Array.from(validatorMap.values());

    // Cleanup setting if selected value is no longer valid
    if (
      selectedValue &&
      property.packagedType === PackagedTypeEnum.MultiSelect &&
      !isSelectedFieldsExistsInDropdown(
        dataToSet,
        selectedValue as MultiSelectDropdownField[]
      )
    ) {
      delete settings[key];
    }
  };

  const setupBEPrismFields = async () => {
    const email = channelIntegration.settings.username;
    const password = channelIntegration.settings.password;
    const channelId = channel._id;
    const integrationId = integration?._id;

    if (!integrationId) {
      setMetaDataError('Integration ID is required');
      setMetaDataLoading(false);

      return;
    }

    setMetaDataLoading(true);

    const configResponse =
      await BuildingEnginesPrism.requestHelpers.getConfigMetaData(
        email,
        password,
        channelId,
        integrationId
      );

    if (configResponse.isError) {
      setMetaDataError(
        configResponse.message ||
          t("You've entered an invalid email or password.")
      );
      setMetaDataLoading(false);

      return;
    }

    const buildingListResponse = configResponse.buildingListResponse;

    setMetadataPopulated(true);
    setMetaDataLoading(false);
    setMetaDataError('');

    const accountId = buildingListResponse?.results?.[0]?.accountId;

    const updatedSettings = {
      ...channelIntegration.settings,
      ...(accountId && { tenantAccountId: accountId }),
    };

    if (accountId) {
      onUpdateChannelIntegration({
        settings: updatedSettings,
      });
    }

    if (buildingListResponse?.results && definition.properties?.buildingIds) {
      const buildingList = buildingListResponse.results?.map(
        (building: { id: string; name: string }) => ({
          _id: building.id,
          _order: 0,
          name: `${building.name} (ID: ${building.id})`,
          value: `${building.id}`,
        })
      );

      setupMultiSelectValidators(
        definition.properties.buildingIds,
        buildingList,
        'buildingIds',
        channelIntegration.settings
      );
    }
  };

  useEffect(() => {
    // fetch existing save config data when open edit config page
    if (
      isBEFFEnabled &&
      (providerValue === IntegrationProviderEnum.BuildingEnginesPrism ||
        providerValue ===
          IntegrationProviderEnum.BuildingEnginesPrismVisitors) &&
      channelIntegration.settings.buildingIds?.length
    ) {
      setupBEPrismFields();
    }
  }, []);

  /**
 * Validates whether all required metadata fields (tagged with 'metadataFieldRequest')
 * have corresponding values in the settings object.

 * This function is typically used to determine whether to disable a button
 * until all dependent fields are filled out.
 */
  const validateMetadataRequestFields = (
    properties: PropertiesInterface,
    settings: ChannelIntegrationEditorProps['channelIntegration']['settings']
  ) => {
    return Object.entries(properties)
      .filter(([, prop]) => prop?.tags?.includes('metadataFieldRequest'))
      .some(([key]) => !settings?.[key]);
  };

  const handleLoadSetting = () => {
    if (
      isBEFFEnabled &&
      (providerValue === IntegrationProviderEnum.BuildingEnginesPrism ||
        providerValue === IntegrationProviderEnum.BuildingEnginesPrismVisitors)
    ) {
      return setupBEPrismFields();
    }

    return null;
  };

  const hideBEPrismFields = (key: string) => {
    if (
      isBEFFEnabled &&
      (providerValue === IntegrationProviderEnum.BuildingEnginesPrism ||
        providerValue ===
          IntegrationProviderEnum.BuildingEnginesPrismVisitors) &&
      ['buildingId', 'name', 'subtitle', 'description'].includes(key)
    ) {
      return true;
    }

    if (
      !isBEFFEnabled &&
      (providerValue === IntegrationProviderEnum.BuildingEnginesPrism ||
        providerValue ===
          IntegrationProviderEnum.BuildingEnginesPrismVisitors) &&
      ['buildingIds', 'loadSettingsButton', 'subHeading'].includes(key)
    ) {
      return true;
    }

    if (
      !isBEFFEnabled &&
      providerValue === IntegrationProviderEnum.BuildingEnginesPrismVisitors &&
      (key === 'username' || key === 'password')
    ) {
      return true;
    }

    if (
      (!isBEFFEnabled || !isBEWebhookEnabled) &&
      providerValue === IntegrationProviderEnum.BuildingEnginesPrism &&
      key === 'disableWebhookNotification'
    ) {
      return true;
    }

    return false;
  };

  return (
    <div className={styles.ChannelIntegrationEditorAutoRenderer}>
      <H4 mb={4}>{definition.friendlyName} Settings</H4>
      {Object.entries(definition.properties).map(([key, property]) => {
        if (property.hidden || hideBEPrismFields(key)) {
          return null;
        }

        if (
          channelIntegration.settings[key] === undefined &&
          property.default !== undefined
        ) {
          onUpdateChannelIntegration({
            settings: {
              ...channelIntegration.settings,
              [key]: property.default,
            },
          });
        }

        if (key === 'disableWebhookNotification') {
          return (
            <div key={key} className={styles.property}>
              <Checkbox 
                className={styles.checkbox}
                selected={!!channelIntegration.settings.disableWebhookNotification}
                value={!!channelIntegration.settings.disableWebhookNotification}
                onChange={() => {
                    const newValue = !channelIntegration.settings.disableWebhookNotification;

                    onUpdateChannelIntegration({
                      settings: {
                        ...channelIntegration.settings,
                        disableWebhookNotification: newValue,
                      },
                    });
                  }}
                text={property.friendlyName}
              />
            </div>
          );
        }

        if (key === IntegrationInputs.LoadSettingsButton) {
          return (
            <div key={key}>
              <LoadSettingsButton
                loading={metaDataLoading}
                error={metaDataError}
                callback={() => handleLoadSetting()}
                disabled={validateMetadataRequestFields(
                  definition.properties,
                  channelIntegration.settings
                )}
                textContent={
                  forCreate
                    ? 'web.admin.channel.integrations.access.access-control-settings.load-cards'
                    : 'web.admin.channel.integrations.access.access-control-settings.reload-cards'
                }
              />
            </div>
          );
        }

        if (key === IntegrationInputs.SubHeading) {
          return (
            <H5 key={key} mb={5}>
              {property?.friendlyName}
            </H5>
          );
        }

        return (
          <div key={key} className={styles.property}>
            <Property
              object={channelIntegration.settings}
              channel={channel}
              label={property.friendlyName || property.name || key}
              // @ts-expect-error ts-migrate(2322) FIXME: Type '{ object: any; channel: Channel; label: ... Remove this comment to see the full error message
              user={user}
              property={property}
              propertyKey={key}
              value={channelIntegration.settings[key]}
              onChange={value =>
                onUpdateChannelIntegration({
                  settings: {
                    ...channelIntegration.settings,
                    [key]: value,
                  },
                })
              }
              onPropertyChange={({ key, value }) =>
                onUpdateChannelIntegration({
                  settings: {
                    ...channelIntegration.settings,
                    [key]: value,
                  },
                })
              }
              {...(isBEFFEnabled && {
                disabled:
                  !metadataPopulated &&
                  property.tags?.includes('metadataFieldResponse'),
              })}
            />
          </div>
        );
      })}
    </div>
  );
}
